import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import useConfirmAlert from '~/components/common/dialog/useConfirmAlert'
import ItemWithBadgesActions from '~/components/dashboard/soapV2/collapsibleRail/summary/item-with-badges/ItemWithBadgesActions'
import { ConfirmAlertType } from '~/constants/DialogNames'
import { unOrderBundleWithoutOrder } from '~/store/actions/orders'
import {
  deleteChargeSheetItems,
  editChargeSheetItem,
  getClientFinanceClientId,
} from '~/store/duck/clientFinanceData'
import { getCurrentUserId } from '~/store/reducers/auth'
import { getSoapId } from '~/store/reducers/soap'
import type { InvoiceLineItem } from '~/types'

export interface ChargeBundleItemButtonsActionsProps {
  disabled: boolean
  item: InvoiceLineItem
  loading?: boolean
  onDelete: () => void
  onUpdateItem: (item: InvoiceLineItem) => void
}

const ChargeBundleItemButtonsActions = ({
  disabled,
  item,
  loading,
  onDelete: onDeleteProp,
  onUpdateItem,
}: ChargeBundleItemButtonsActionsProps) => {
  const dispatch = useDispatch()

  const { t } = useTranslation('Soap')

  const soapId = useSelector(getSoapId)
  const modifierId = useSelector(getCurrentUserId)
  const clientId = useSelector(getClientFinanceClientId)

  const [openConfirmAlert] = useConfirmAlert({
    type: ConfirmAlertType.SOAP_ORDER,
  })
  const onDeleteBundle = () => {
    if ((item.group && soapId) || item.items) {
      openConfirmAlert({
        message: t('Soap:SOAP_ORDERS.CONFIRM_ALERT_MESSAGE'),
        onConfirm: (proceed) => {
          if (proceed) {
            const { items } = item
            if (soapId && item.group) {
              dispatch(unOrderBundleWithoutOrder(item.group, soapId))
            } else if (items && clientId && modifierId) {
              const deleteItems = items.map((itm) => ({
                id: itm.id,
                expectedModificationDate: itm.modificationDate,
              }))
              dispatch(
                deleteChargeSheetItems({
                  items: deleteItems,
                  modifierId,
                  clientId,
                }),
              )
            }
            onDeleteProp()
          }
        },
      })
    }
  }

  const onDeclineBundle = () => {
    dispatch(
      editChargeSheetItem({
        updateItemInput: (item?.items || []).map((inlineItem) => ({
          id: inlineItem.id,
          expectedModification: inlineItem.modificationDate,
          expectedSoapLogModification: inlineItem.soapLogModificationDate,
          declined: !item.declined,
        })),
      }),
    )
    onUpdateItem({
      ...item,
      declined: !item.declined,
      items: item.items?.map((it) => ({
        ...it,
        declined: !item.declined,
      })),
    })
  }

  return (
    <ItemWithBadgesActions
      hideDivider
      align="flex-start"
      declined={item.declined}
      editDisabled={disabled}
      loading={loading}
      size="large"
      onDecline={onDeclineBundle}
      onDelete={onDeleteBundle}
    />
  )
}

export default ChargeBundleItemButtonsActions
